import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { useOrgContext } from '@/hooks/useOrgContext';
import { useInvoiceForm } from '@/hooks/useInvoiceForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ArrowRight, Loader2, Send, Save } from 'lucide-react';
import { toast } from 'sonner';
import { InvoiceLineItems } from '@/components/insurance/InvoiceLineItems';
import type { IInvoiceFormState } from '@/hooks/useInvoiceForm';

// LOCAL_TYPE_JUSTIFIED: local policy shape mirrors listByCompany return; avoids circular import from convex mutation type pollution
interface ICompanyPolicy {
  _id: Id<'insurancePolicies'>;
  policyNumber: string;
  insuranceTypeName?: string;
  premium?: number;
}

// LOCAL_TYPE_JUSTIFIED: step component props are private to this file and tightly coupled to local hook shape
interface IStep1Props {
  companies: Array<{ _id: string; name: string; status: string }>;
  companyId: string | null;
  onSelect: (id: Id<'companies'>) => void;
  onNext: () => void;
}

function InvoiceStep1({ companies, companyId, onSelect, onNext }: IStep1Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Company</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Select
          value={companyId ?? ''}
          onValueChange={val => onSelect(val as Id<'companies'>)} // AS_JUSTIFIED: Id cast from Select string value
        >
          <SelectTrigger>
            <SelectValue placeholder="Choose a company" />
          </SelectTrigger>
          <SelectContent>
            {companies
              .filter(c => c.status === 'active')
              .map(c => (
                <SelectItem key={c._id} value={c._id}>
                  {c.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <div className="flex justify-end">
          <Button disabled={!companyId} onClick={onNext}>
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// LOCAL_TYPE_JUSTIFIED: step component props are private to this file and tightly coupled to local hook shape
interface IStep2Props {
  policies: Array<{ _id: string; policyNumber: string; insuranceTypeName?: string }>;
  selectedPolicyIds: Set<string>;
  onToggle: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}

function InvoiceStep2({ policies, selectedPolicyIds, onToggle, onBack, onNext }: IStep2Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Policies</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {policies.map(policy => (
          <div
            key={policy._id}
            className="flex items-center gap-3 p-3 border border-border rounded-lg"
          >
            <Checkbox
              checked={selectedPolicyIds.has(policy._id)}
              onCheckedChange={() => onToggle(policy._id)}
            />
            <div>
              <p className="text-sm font-medium text-foreground">{policy.policyNumber}</p>
              <p className="text-xs text-muted-foreground">{policy.insuranceTypeName}</p>
            </div>
          </div>
        ))}
        {policies.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No active policies found for this company&apos;s employees.
          </p>
        )}
        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button disabled={selectedPolicyIds.size === 0} onClick={onNext}>
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// LOCAL_TYPE_JUSTIFIED: step component props are private to this file and tightly coupled to local hook shape
interface IStep3Props {
  form: IInvoiceFormState;
  bankAccounts: Array<{
    _id: string;
    bankName: string;
    accountHolderName: string;
    isPrimary: boolean;
  }>;
  subtotal: number;
  total: number;
  isSaving: boolean;
  isSending: boolean;
  onSetField: <K extends keyof IInvoiceFormState>(key: K, value: IInvoiceFormState[K]) => void;
  onRemoveLineItem: (index: number) => void;
  onBack: () => void;
  onSaveDraft: () => void;
  onSend: () => void;
}

function InvoiceStep3({
  form,
  bankAccounts,
  subtotal,
  total,
  isSaving,
  isSending,
  onSetField,
  onRemoveLineItem,
  onBack,
  onSaveDraft,
  onSend,
}: IStep3Props) {
  const isSubmitting = isSaving || isSending;
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Invoice Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invoice-number">Invoice Number</Label>
              <Input
                id="invoice-number"
                value={form.invoiceNumber}
                onChange={e => onSetField('invoiceNumber', e.target.value)}
                placeholder="INV-001"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="due-date">Due Date</Label>
              <Input
                id="due-date"
                type="date"
                value={form.dueDate}
                onChange={e => onSetField('dueDate', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bank-account">Bank Account</Label>
              <Select
                value={form.bankAccountId ?? ''}
                onValueChange={val => onSetField('bankAccountId', val as Id<'orgBankAccounts'>)} // AS_JUSTIFIED: Id cast from Select string value
              >
                <SelectTrigger id="bank-account">
                  <SelectValue placeholder="Select bank account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(ba => (
                    <SelectItem key={ba._id} value={ba._id}>
                      {ba.bankName} — {ba.accountHolderName}
                      {ba.isPrimary ? ' (Primary)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax">Tax Amount</Label>
              <Input
                id="tax"
                type="number"
                min="0"
                step="0.01"
                value={form.tax}
                onChange={e => onSetField('tax', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent>
          <InvoiceLineItems
            lineItems={form.lineItems}
            currency={form.currency}
            editable
            onRemove={onRemoveLineItem}
            subtotal={subtotal}
            tax={form.tax}
            total={total}
          />
        </CardContent>
      </Card>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onSaveDraft} disabled={isSubmitting}>
            {isSaving ? (
              <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save aria-hidden="true" className="w-4 h-4 mr-2" />
            )}
            Save Draft
          </Button>
          <Button onClick={onSend} disabled={isSubmitting}>
            {isSending ? (
              <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send aria-hidden="true" className="w-4 h-4 mr-2" />
            )}
            Send Invoice
          </Button>
        </div>
      </div>
    </div>
  );
}

export function InvoiceFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id && id !== 'new';
  const { currentOrg } = useOrgContext();

  const {
    form,
    setStep,
    setCompany,
    togglePolicy,
    setLineItems,
    removeLineItem,
    setField,
    initFromExisting,
  } = useInvoiceForm();

  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const invoiceId = isEditing ? (id as Id<'companyInvoices'>) : undefined; // AS_JUSTIFIED: one-time Id cast from route param
  const existingInvoice = useQuery(api.invoices.queries.get, invoiceId ? { invoiceId } : 'skip');

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (!isEditing || initialized || !existingInvoice) return;
    initFromExisting({
      companyId: existingInvoice.companyId,
      lineItems: existingInvoice.lineItems,
      invoiceNumber: existingInvoice.invoiceNumber,
      bankAccountId: existingInvoice.bankAccountId,
      dueDate: existingInvoice.dueDate,
      tax: existingInvoice.tax,
      currency: existingInvoice.currency,
    });
    setInitialized(true);
  }, [isEditing, initialized, existingInvoice, initFromExisting]);

  const companies = useQuery(
    api.companies.queries.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const companyPolicies = useQuery(
    api.insurance.policies.listByCompany,
    form.companyId ? { companyId: form.companyId } : 'skip'
  );
  const bankAccounts = useQuery(
    api.bankAccounts.queries.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const createInvoice = useMutation(api.invoices.mutations.create);
  const updateInvoice = useMutation(api.invoices.mutations.update);
  const generateAndSend = useAction(api.invoices.actions.generateAndSendInvoice);

  const typedPolicies = useMemo(
    () => (companyPolicies ?? []) as ICompanyPolicy[], // AS_JUSTIFIED: listByCompany return type is any due to circular mutation error; actual shape matches ICompanyPolicy
    [companyPolicies]
  );

  const handleToStep3 = useCallback(() => {
    const selected = typedPolicies.filter(p => form.selectedPolicyIds.has(p._id));
    const items = selected.map(p => ({
      policyId: p._id,
      description: `Policy ${p.policyNumber}${p.insuranceTypeName ? ` - ${p.insuranceTypeName}` : ''}`,
      amount: p.premium ?? 0,
    }));
    setLineItems(items);
    setStep(3);
  }, [typedPolicies, form.selectedPolicyIds, setLineItems, setStep]);

  const subtotal = useMemo(
    () => form.lineItems.reduce((sum, item) => sum + item.amount, 0),
    [form.lineItems]
  );
  const total = subtotal + form.tax;

  const buildPayload = useCallback(
    () => ({
      orgId: currentOrg!._id,
      companyId: form.companyId!,
      lineItems: form.lineItems,
      tax: form.tax > 0 ? form.tax : undefined,
      currency: form.currency,
      bankAccountId: form.bankAccountId || undefined,
      dueDate: form.dueDate ? new Date(form.dueDate).getTime() : undefined,
    }),
    [currentOrg, form]
  );

  const handleSaveDraft = async () => {
    if (!currentOrg || !form.companyId) return;
    setIsSaving(true);
    try {
      if (isEditing && invoiceId) {
        await updateInvoice({
          invoiceId,
          lineItems: form.lineItems,
          tax: form.tax > 0 ? form.tax : undefined,
          currency: form.currency,
          bankAccountId: form.bankAccountId || undefined,
          dueDate: form.dueDate ? new Date(form.dueDate).getTime() : undefined,
        });
        toast.success('Invoice updated');
      } else {
        await createInvoice(buildPayload());
        toast.success('Invoice saved as draft');
      }
      navigate('/insurance/invoices');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save invoice');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!currentOrg || !form.companyId) return;
    if (!form.bankAccountId) {
      toast.error('Please select a bank account');
      return;
    }
    setIsSending(true);
    try {
      let targetInvoiceId: Id<'companyInvoices'>;
      if (isEditing && invoiceId) {
        await updateInvoice({
          invoiceId,
          lineItems: form.lineItems,
          tax: form.tax > 0 ? form.tax : undefined,
          currency: form.currency,
          bankAccountId: form.bankAccountId || undefined,
          dueDate: form.dueDate ? new Date(form.dueDate).getTime() : undefined,
        });
        targetInvoiceId = invoiceId;
      } else {
        targetInvoiceId = await createInvoice(buildPayload());
      }
      await generateAndSend({ invoiceId: targetInvoiceId });
      toast.success('Invoice sent');
      navigate('/insurance/invoices');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-full bg-background-secondary p-8">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/insurance/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEditing ? 'Edit Invoice' : 'Create Invoice'}
        </h1>
      </div>

      <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground">
        <span className={form.step >= 1 ? 'text-foreground font-medium' : ''}>
          1. Select Company
        </span>
        <ArrowRight className="w-3 h-3" />
        <span className={form.step >= 2 ? 'text-foreground font-medium' : ''}>
          2. Select Policies
        </span>
        <ArrowRight className="w-3 h-3" />
        <span className={form.step >= 3 ? 'text-foreground font-medium' : ''}>
          3. Configure & Send
        </span>
      </div>

      {form.step === 1 && (
        <InvoiceStep1
          companies={companies ?? []}
          companyId={form.companyId}
          onSelect={setCompany}
          onNext={() => setStep(2)}
        />
      )}
      {form.step === 2 && (
        <InvoiceStep2
          policies={typedPolicies}
          selectedPolicyIds={form.selectedPolicyIds}
          onToggle={togglePolicy}
          onBack={() => setStep(1)}
          onNext={handleToStep3}
        />
      )}
      {form.step === 3 && (
        <InvoiceStep3
          form={form}
          bankAccounts={bankAccounts ?? []}
          subtotal={subtotal}
          total={total}
          isSaving={isSaving}
          isSending={isSending}
          onSetField={setField}
          onRemoveLineItem={removeLineItem}
          onBack={() => setStep(2)}
          onSaveDraft={handleSaveDraft}
          onSend={handleSendInvoice}
        />
      )}
    </div>
  );
}
